import { InfoCircleOutlined } from '@ant-design/icons';
import { type TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { type IdentifySelectionMode } from '../drawing/identify/useMapIdentifyInteractions.ts';
import { type MapDrawingMode } from '../drawing/useMapDrawingInteractions.tsx';
import { MapWidgetSelectionShape } from '../IMapWidget.ts';

/** Suggerimento contestuale: gesto principale ed eventuale gesto secondario. */
interface IToolHint {
  primary: string;
  /** Gesto aggiuntivo (es. modifica/spostamento), presente solo in disegno. */
  secondary?: string;
}

/**
 * Restituisce il suggerimento d'uso per la modalità/forma correnti, o `null`
 * quando non c'è alcun gesto da spiegare (modalità visualizzazione).
 */
function getToolHint(
  t: TFunction,
  params: {
    mode: MapDrawingMode;
    activeShape: MapWidgetSelectionShape;
    identifySelectionMode: IdentifySelectionMode;
  },
): IToolHint | null {
  const { mode, activeShape, identifySelectionMode } = params;

  switch (mode) {
    case 'draw':
      // In disegno sono attive anche Modify (vertici) e Translate (geometria
      // intera): il gesto di editing è documentato nella riga secondaria.
      switch (activeShape) {
        case MapWidgetSelectionShape.Point:
          return {
            primary: t('mappa.suggerimenti.disegna-punto', {
              defaultValue: 'Clicca sulla mappa per posizionare un punto.',
            }),
            secondary: t('mappa.suggerimenti.disegna-punto-sposta', {
              defaultValue: 'Trascina un punto esistente per spostarlo.',
            }),
          };
        case MapWidgetSelectionShape.Line:
          return {
            primary: t('mappa.suggerimenti.disegna-linea', {
              defaultValue:
                'Clicca per aggiungere punti · doppio clic per terminare la linea.',
            }),
            secondary: t('mappa.suggerimenti.disegna-modifica', {
              defaultValue:
                "Trascina un vertice per modificarlo o l'intera geometria per spostarla.",
            }),
          };
        case MapWidgetSelectionShape.Polygon:
          return {
            primary: t('mappa.suggerimenti.disegna-poligono', {
              defaultValue:
                "Clicca per aggiungere punti · doppio clic per chiudere l'area.",
            }),
            secondary: t('mappa.suggerimenti.disegna-modifica', {
              defaultValue:
                "Trascina un vertice per modificarlo o l'intera geometria per spostarla.",
            }),
          };
      }
      break;
    case 'delete':
      return {
        primary: t('mappa.suggerimenti.elimina', {
          defaultValue: 'Clicca su una geometria per eliminarla.',
        }),
      };
    case 'identify':
      switch (identifySelectionMode) {
        case 'point':
          return {
            primary: t('mappa.suggerimenti.interroga-punto', {
              defaultValue:
                'Clicca sulla mappa per interrogare i livelli in quel punto.',
            }),
          };
        case 'rectangle':
          return {
            primary: t('mappa.suggerimenti.interroga-rettangolo', {
              defaultValue:
                "Clicca e trascina per definire l'area da interrogare.",
            }),
          };
        case 'polygon':
          return {
            primary: t('mappa.suggerimenti.interroga-poligono', {
              defaultValue:
                "Clicca per aggiungere punti · doppio clic per chiudere l'area da interrogare.",
            }),
          };
      }
      break;
    case 'view':
      return null;
  }

  return null;
}

const enter = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Contenitore del suggerimento: allineato in basso a destra, sopra la barra di
 * stato inferiore (MapControls, ~48px con `bottom: 0`) così non la copre.
 * Non intercetta i click (pointer-events: none) → pan/zoom restano usabili.
 */
const HintContainer = styled.div`
  position: absolute;
  right: 0.5rem;
  bottom: 3.5rem;
  max-width: min(92%, 520px);
  display: flex;
  justify-content: flex-end;
  pointer-events: none;
`;

/**
 * Stile "hint" volutamente diverso dai menu/controlli (chip chiari con bordo):
 * fondo scuro semitrasparente, compatto e discreto, così non si confonde con
 * la UI dei controlli ed è poco invasivo.
 */
const HintPill = styled.div`
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  max-width: 100%;
  padding: 4px 10px;
  background: rgba(0, 0, 0, 0.72);
  color: #fff;
  border-radius: ${({ theme }) => theme.borderRadius}px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
  animation: ${enter} 0.25s ease;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const HintIcon = styled(InfoCircleOutlined)`
  flex-shrink: 0;
  margin-top: 2px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
`;

const HintText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  text-align: left;
  line-height: 1.35;
`;

const HintPrimary = styled.span`
  font-size: 12px;
  font-weight: 500;
`;

const HintSecondary = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.72);
`;

export interface IMapToolHintProps {
  mode: MapDrawingMode;
  activeShape: MapWidgetSelectionShape;
  identifySelectionMode: IdentifySelectionMode;
}

/**
 * Mini-tutorial contestuale: mostra in basso sulla mappa il gesto d'uso dello
 * strumento attivo (disegna/interroga/elimina), aggiornandosi in base a
 * modalità e forma selezionate. È una region `aria-live` così i cambi di
 * strumento vengono annunciati agli screen reader.
 */
export function MapToolHint(props: IMapToolHintProps) {
  const { mode, activeShape, identifySelectionMode } = props;
  const { t } = useTranslation();

  const hint = getToolHint(t, { mode, activeShape, identifySelectionMode });
  if (!hint) return null;

  return (
    <HintContainer>
      <HintPill aria-live="polite">
        <HintIcon aria-hidden="true" />
        <HintText>
          <HintPrimary>{hint.primary}</HintPrimary>
          {hint.secondary && <HintSecondary>{hint.secondary}</HintSecondary>}
        </HintText>
      </HintPill>
    </HintContainer>
  );
}
